import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useRefreshOnNotification } from '../hooks/useRefreshOnNotification';
import { authApi } from '../services/api';
import { apiErrorCode } from '../utils/apiError';
import type { OpenInvitation } from '../types';
import { Button } from './Button';

type Feedback = { tone: 'success' | 'danger'; text: string };

/** The Hantverkare's facts, the same on the start page and the decision page. */
export function InvitationCompanyFacts({ contact, city, organizationNumber }: {
  contact?: string | null;
  city?: string | null;
  organizationNumber?: string | null;
}) {
  const { t } = useTranslation();
  if (!contact && !city && !organizationNumber) return null;
  return (
    <dl className="invitation-company-facts">
      {contact && (<><dt>{t('invitationDecision.company.contact')}</dt><dd>{contact}</dd></>)}
      {city && (<><dt>{t('invitationDecision.company.city')}</dt><dd>{city}</dd></>)}
      {organizationNumber && (<><dt>{t('invitationDecision.company.organizationNumber')}</dt><dd>{organizationNumber}</dd></>)}
    </dl>
  );
}

/**
 * Invitations to the Beställare's verified email that wait for them, for
 * someone who registered without the link (ClickUp 869fmx898). Accepting links
 * the account to the Hantverkare; projects show once an agreement is sent.
 */
export function OpenInvitations({ onAccepted }: { onAccepted?: () => void }) {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [invitations, setInvitations] = useState<OpenInvitation[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const load = useCallback(
    () => authApi.getOpenInvitations().then(setInvitations).catch(() => setInvitations([])),
    []
  );
  useEffect(() => { void load(); }, [load]);
  useRefreshOnNotification(load);

  const answer = async (invitation: OpenInvitation, accept: boolean) => {
    const company = invitation.companyName || t('openInvitations.yourContractor');
    setBusyId(invitation.id);
    setFeedback(null);
    try {
      if (accept) {
        await authApi.acceptOpenInvitation(invitation.id);
        await refreshUser();
        onAccepted?.();
      } else {
        await authApi.declineOpenInvitation(invitation.id);
      }
      setFeedback({
        tone: 'success',
        text: t(accept ? 'openInvitations.accepted' : 'openInvitations.declined', { company })
      });
      await load();
    } catch (error) {
      setFeedback({
        tone: 'danger',
        text: apiErrorCode(error) === 'INVITATION_PERSONA_MISMATCH'
          ? t('invitationDecision.personaMismatchDescription')
          : t('openInvitations.error')
      });
    } finally {
      setBusyId(null);
    }
  };

  if (invitations.length === 0 && !feedback) return null;

  return (
    <section className="mb-4" aria-label={t('openInvitations.label')} data-testid="open-invitations">
      {feedback && (
        <div className={`alert alert-${feedback.tone}`} role={feedback.tone === 'danger' ? 'alert' : 'status'}>
          {feedback.text}
        </div>
      )}
      {invitations.map(invitation => {
        const company = invitation.companyName || t('openInvitations.yourContractor');
        return (
          <div key={invitation.id} className="card open-invitation mb-3" data-testid={`open-invitation-${invitation.id}`}>
            <div className="card-body">
              <p className="invitation-company-kicker mb-1">{t('openInvitations.kicker')}</p>
              <h5 className="fw-bold mb-2 text-break">{t('openInvitations.title', { company })}</h5>
              <InvitationCompanyFacts
                contact={invitation.inviterName}
                city={invitation.city}
                organizationNumber={invitation.organizationNumber}
              />
              <p className="small text-muted mb-3">{t('invitationDecision.company.explanation', { company })}</p>
              <div className="open-invitation-actions">
                <Button
                  type="button"
                  isLoading={busyId === invitation.id}
                  disabled={busyId !== null}
                  onClick={() => void answer(invitation, true)}
                  data-testid={`open-invitation-accept-${invitation.id}`}
                >
                  {t('openInvitations.accept')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={busyId !== null}
                  onClick={() => void answer(invitation, false)}
                  data-testid={`open-invitation-decline-${invitation.id}`}
                >
                  {t('openInvitations.decline')}
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}
